import React, { useState, useEffect, useContext } from "react"
import {
  Button,
  Col,
  Form,
  Input,
  Label,
  Row,
  UncontrolledAlert,
  Card,
  CardBody,
  Modal,
} from "reactstrap"
import TenantConnectionContext from "./TenantConnectionContext"
import LoadingOverlay from "react-loading-overlay"
import Error from "pages/NotificationModal/Error"
import Success from "./Success"
import Confirmation from "./Confirmation"
import SecretInput from "./SecretInput"

// REVENOVA (Salesforce) outbound auth is api_type-discriminated: all credentials
// live in config.oauth_config, keyed by an `auth_type` selector, and the endpoint
// is the top-level url column. This mirrors the backend auth_schema REVENOVA spec.
const REVENOVA_AUTH_TYPES = [
  { value: "client_credentials", label: "OAuth 2.0 — Client Credentials" },
  { value: "jwt", label: "OAuth 2.0 — JWT Bearer (server-to-server)" },
  { value: "password", label: "OAuth 2.0 — Username / Password" },
]

// field sets per auth_type (must match REVENOVA_AUTH_TYPES in auth_schema.py)
const REVENOVA_FIELDS = {
  client_credentials: [
    { name: "token_url", label: "Token URL" },
    { name: "clientId", label: "Consumer Key (Client ID)" },
    { name: "clientSecret", label: "Consumer Secret", secret: true },
  ],
  jwt: [
    { name: "clientId", label: "Consumer Key (Client ID)" },
    { name: "userId", label: "Salesforce Username" },
    {
      name: "private_key",
      label: "Private Key (PEM)",
      secret: true,
      type: "textarea",
    },
  ],
  password: [
    { name: "token_url", label: "Token URL" },
    { name: "clientId", label: "Consumer Key (Client ID)" },
    { name: "clientSecret", label: "Consumer Secret", secret: true },
    { name: "userId", label: "Salesforce Username" },
    { name: "password", label: "Password", secret: true },
    { name: "scope", label: "Scope (optional)", optional: true },
  ],
}

// Matches the exact masked shapes auth_schema emits ("*", "**", "X**Y"); a masked
// value on submit means "keep the stored secret".
const isMasked = v => typeof v === "string" && /^(\*{1,2}|.\*\*.)$/.test(v)
const hasVal = v => v != null && String(v).trim() !== ""

function fieldsFor(authType) {
  return REVENOVA_FIELDS[authType] || []
}

const RevenovaAuthForm = ({ hideForm }) => {
  const {
    loading,
    success,
    error,
    errorMessage,
    setSuccess,
    setError,
    tenantData,
    submitUpdate,
    revealSecret,
    updateResponse,
  } = useContext(TenantConnectionContext)

  const [authType, setAuthType] = useState("")
  const [url, setUrl] = useState("")
  const [fields, setFields] = useState({})
  const [errorMsg, setErrorMsg] = useState("")
  const [showConfirmation, setShowConfirmation] = useState(false)

  useEffect(() => {
    if (!tenantData) return
    setAuthType(tenantData.auth_type || "client_credentials")
    setUrl(tenantData.url || "")
    setFields({ ...((tenantData.auth || {}).revenova || {}) })
  }, [tenantData])

  const handleAuthTypeChange = e => {
    const next = e.target.value
    setAuthType(next)
    // The stored fields only belong to the originally-loaded auth_type; switching
    // to a different type starts blank (new credentials required).
    const stored =
      next === tenantData?.auth_type ? (tenantData.auth || {}).revenova : null
    setFields({ ...(stored || {}) })
  }

  const setField = (name, value) =>
    setFields(prev => ({ ...prev, [name]: value }))

  const renderField = field => {
    const value = fields[field.name]
    if (field.secret) {
      return (
        <>
          <SecretInput
            value={value}
            onChange={v => setField(field.name, v)}
            label={field.label}
            revealPath={`oauth_config.${field.name}`}
            onReveal={revealSecret}
            multiline={field.type === "textarea"}
          />
          {field.type === "textarea" && (
            <small className="text-muted">
              Leave the masked value to keep the stored key, or paste a new PEM to
              replace it.
            </small>
          )}
        </>
      )
    }
    return (
      <Input
        type="text"
        value={value || ""}
        onChange={e => setField(field.name, e.target.value)}
        placeholder={`Enter ${field.label.toLowerCase()}`}
      />
    )
  }

  const fieldRow = (label, node) => (
    <Row className="mb-2" key={label}>
      <Label className="col-sm-3 col-form-label">{label}</Label>
      <Col sm={6} className="ms-4">
        {node}
      </Col>
    </Row>
  )

  const handleSubmit = () => {
    setErrorMsg("")
    if (!authType) {
      setErrorMsg("Please select an authentication type")
      return
    }
    if (!hasVal(url)) {
      setErrorMsg("URL is required")
      return
    }
    const missing = fieldsFor(authType)
      .filter(f => !f.optional)
      .filter(f => {
        const v = fields[f.name]
        return f.secret ? !(hasVal(v) || isMasked(v)) : !hasVal(v)
      })
      .map(f => f.label)
    if (missing.length) {
      setErrorMsg(`Required: ${missing.join(", ")}`)
      return
    }
    setShowConfirmation(true)
  }

  const handleConfirmSubmit = async () => {
    setShowConfirmation(false)
    const revenova = {}
    fieldsFor(authType).forEach(f => {
      revenova[f.name] = fields[f.name] || ""
    })
    const payload = {
      auth_type: authType,
      url,
      auth: { revenova },
    }
    await submitUpdate(payload)
  }

  const handleCancel = () => {
    setShowConfirmation(false)
    hideForm()
  }
  const closeSuccess = () => {
    setSuccess(false)
    hideForm()
  }
  const togError = () => setError(false)

  if (!tenantData) {
    return (
      <LoadingOverlay active={loading} spinner>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <h5>Loading...</h5>
        </div>
      </LoadingOverlay>
    )
  }

  return (
    <>
      <Modal isOpen={success} toggle={closeSuccess} centered size="md">
        <Success data={updateResponse} cancel={closeSuccess} />
      </Modal>
      <Modal isOpen={error} toggle={togError} centered size="sm">
        <Error
          message={errorMessage || "Something went wrong"}
          closeModal={togError}
        />
      </Modal>
      <Modal
        isOpen={showConfirmation}
        toggle={() => setShowConfirmation(false)}
        centered
        size="sm"
      >
        <Confirmation
          message="Do you want to save these changes?"
          confirm={handleConfirmSubmit}
          cancel={() => setShowConfirmation(false)}
        />
      </Modal>

      <LoadingOverlay active={loading} spinner>
        <Card className="p-3">
          <div className="mb-3">
            <h6 className="text-primary">
              Edit Revenova (Salesforce) Outbound Auth
            </h6>
          </div>
          <CardBody className="p-0">
            <Card outline color="primary" className="border mt-2 mb-3">
              <CardBody className="p-3">
                {errorMsg && (
                  <div className="mb-3">
                    <UncontrolledAlert color="danger" role="alert">
                      {errorMsg}
                    </UncontrolledAlert>
                  </div>
                )}
                <Form>
                  {fieldRow(
                    "URL",
                    <Input
                      type="text"
                      value={url}
                      placeholder="Salesforce apexrest endpoint"
                      onChange={e => setUrl(e.target.value)}
                    />
                  )}
                  {fieldRow(
                    "Auth Type *",
                    <Input
                      type="select"
                      value={authType}
                      onChange={handleAuthTypeChange}
                    >
                      <option value="">Select type</option>
                      {REVENOVA_AUTH_TYPES.map(t => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </Input>
                  )}

                  {fieldsFor(authType).map(f =>
                    fieldRow(f.label, renderField(f))
                  )}
                </Form>
              </CardBody>
            </Card>

            <div className="d-flex justify-content-end mt-3 me-4">
              <div className="d-flex gap-2">
                <Button onClick={handleSubmit} className="btn" color="primary">
                  Save
                </Button>
                <Button
                  onClick={handleCancel}
                  className="btn"
                  color="secondary"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </LoadingOverlay>
    </>
  )
}

export default RevenovaAuthForm
